import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Grid, Plus, Search, Edit2, Trash2, Tag, Folder } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface Category {
  _id: string;
  name: string;
  description?: string;
  parent?: string | { _id: string; name: string };
  isActive: boolean;
  createdAt?: string;
}

const CategorysPage: React.FC = () => {
  const [categorys, setCategorys] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    parent: '', 
    isActive: true 
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchCategorys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCategories();
      const data = response.data;

      // Normalize API response to ALWAYS be an array to avoid runtime errors
      let list: Category[] = [];
      if (Array.isArray(data?.categories)) {
        list = data.categories;
      } else if (Array.isArray(data)) {
        list = data;
      } else {
        list = [];
      }

      // Ensure all categories have required fields with defaults
      list = list.map((cat: any) => ({
        _id: cat._id || '',
        name: cat.name || '',
        description: cat.description || '',
        parent: cat.parent || null,
        isActive: cat.isActive !== undefined ? cat.isActive : true,
        createdAt: cat.createdAt || cat.created_at || ''
      }));

      setCategorys(list);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      // Set empty array on error to prevent blank page
      setCategorys([]);
      toast.error(error.response?.data?.error || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategorys();
  }, [fetchCategorys]);

  const handleOpenModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      
      // Extract parent ID - handle both populated object and string ID
      let parentId = '';
      if (category.parent) {
        if (typeof category.parent === 'object' && category.parent._id) {
          parentId = category.parent._id;
        } else if (typeof category.parent === 'string') {
          parentId = category.parent;
        }
      }
      
      setFormData({ 
        name: category.name || '', 
        description: category.description || '',
        parent: parentId,
        isActive: category.isActive !== undefined ? category.isActive : true
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '', parent: '', isActive: true });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', parent: '', isActive: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      setSubmitting(true);
      // Prepare data: convert empty string parent to null, ensure isActive is boolean
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        parent: formData.parent && formData.parent !== '' ? formData.parent : null,
        isActive: formData.isActive
      };

      if (editingCategory) {
        await apiService.updateCategory(editingCategory._id, submitData);
        toast.success('Category updated successfully');
      } else {
        await apiService.createCategory(submitData);
        toast.success('Category created successfully');
      }
      
      // Close modal first
      handleCloseModal();
      
      // Then refresh the list with error handling
      try {
        await fetchCategorys();
      } catch (fetchError: any) {
        console.error('Error refreshing categories after save:', fetchError);
        // Don't show error toast here - the save was successful
        // Just log it and let the user see the success message
      }
    } catch (error: any) {
      console.error('Error saving category:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to save category';
      toast.error(errorMsg);
      // Don't close modal on error so user can fix and retry
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await apiService.deleteCategory(id);
      toast.success('Category deleted successfully');
      fetchCategorys();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.error || 'Failed to delete category');
    }
  };

  const filteredCategorys = Array.isArray(categorys)
    ? categorys.filter((category) =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Get available parent categories (exclude self when editing)
  const getAvailableParents = () => {
    return categorys.filter(cat => {
      if (editingCategory && cat._id === editingCategory._id) {
        return false; // Can't be its own parent
      }
      return true;
    });
  };

  // Format date to DD/MM/YYYY
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  };

  const columns = [
    { 
      header: 'CATEGORY', 
      accessor: (row: Category) => {
        try {
          return (
            <div className="flex items-center">
              <Tag className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">{row.name || '-'}</span>
            </div>
          );
        } catch (err) {
          console.error('Error rendering category name:', err, row);
          return <span className="font-medium">{row.name || '-'}</span>;
        }
      }
    },
    { 
      header: 'PARENT CATEGORY',
      accessor: (row: Category) => {
        try {
          // Check if parent exists and is populated
          const hasParent = row.parent && (
            (typeof row.parent === 'object' && row.parent._id && row.parent.name) ||
            (typeof row.parent === 'string' && row.parent !== '')
          );
          
          if (hasParent) {
            let parentName = '';
            if (typeof row.parent === 'object' && row.parent.name) {
              parentName = row.parent.name;
            } else if (typeof row.parent === 'string') {
              // Try to find parent in the list
              const parentCat = categorys.find(c => c._id === row.parent);
              parentName = parentCat?.name || 'Unknown';
            }
            
            if (parentName) {
              return (
                <div className="flex items-center">
                  <Folder className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{parentName}</span>
                </div>
              );
            }
          }
          
          return <span className="text-gray-500">Root Category</span>;
        } catch (err) {
          console.error('Error rendering parent category:', err, row);
          return <span className="text-gray-500">Root Category</span>;
        }
      }
    },
    { 
      header: 'DESCRIPTION', 
      accessor: (row: Category) => row.description || '-' 
    },
    {
      header: 'STATUS',
      accessor: (row: Category) => {
        if (row.isActive === undefined) return '-';
        return row.isActive ? 'Active' : 'Inactive';
      },
    },
    {
      header: 'CREATED',
      accessor: (row: Category) => formatDate(row.createdAt),
    },
    {
      header: 'ACTIONS',
      accessor: (row: Category) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleOpenModal(row)}
            className="text-blue-600 hover:text-blue-700 p-1"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-gray-600 hover:text-gray-700 p-1"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categorys</h1>
          <p className="mt-2 text-sm text-gray-600">Manage category information</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Category</Button>
      </motion.div>

      <Card padding="md">
        <Input placeholder="Search categorys..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} icon={<Search className="w-5 h-5" />} fullWidth />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredCategorys.length > 0 ? (
          <Table data={filteredCategorys} columns={columns} emptyMessage="No categorys found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <Grid className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No categorys yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first category.</p>
            <div className="mt-6"><Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Category</Button></div>
          </div>
        )}
      </Card>

      <Modal isOpen={modalOpen} onClose={handleCloseModal} title={editingCategory ? 'Edit Category' : 'Add New Category'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input 
            label="Name *" 
            value={formData.name} 
            onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
            placeholder="Enter category name" 
            required 
            fullWidth 
          />
          
          <Input 
            label="Description" 
            value={formData.description} 
            onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
            placeholder="Enter category description" 
            fullWidth 
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent Category <span className="text-gray-500">(Optional)</span>
            </label>
            <select
              value={formData.parent || ''}
              onChange={(e) => {
                const newParent = e.target.value;
                setFormData({ ...formData, parent: newParent });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">No Parent (Root Category)</option>
              {getAvailableParents().map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.isActive ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CategorysPage;

